import React, { useEffect, useState } from 'react';
import {
  Button,
  Form,
  Input,
  Modal,
  Select,
  message
} from 'antd';
import { getRoleNames, type RoleName } from '../../../service/user.service';
import { addWorkspaceMember } from '../../../service/workspaceMembers.service';
import PersonPicker from '../people/PersonPicker';
import { grantExceedsFrom403 } from '../../../service/capability.service';
import { moduleLabel } from '../../../config/capabilities';

type AddForm = { userId: string; roleKey: string };

const roleValue = (role: RoleName) => role.custom_role_id ? `custom:${role.custom_role_id}` : `builtin:${role.name}`;
const roleBody = (value: string) => value.startsWith('custom:')
  ? { custom_role_id: Number(value.slice('custom:'.length)) }
  : { role: value.slice('builtin:'.length) };

/** A 403 GRANT_EXCEEDS_GRANTOR as a sentence listing the modules the ceiling refused. */
const grantMessage = (error: any): string | null => {
  const modules = grantExceedsFrom403(error);
  if (!modules) return null;
  return `You cannot grant more than you hold. Exceeds your access on: ${modules.map(moduleLabel).join(', ') || 'unknown modules'}.`;
};

/**
 * Workspaces do not invite: somebody the organisation already has is picked and given a
 * workspace role. New people are invited to the organisation instead.
 */
const AddFromOrganisation: React.FC<{
  open: boolean;
  workspaceId: number;
  workspaceName: string;
  onClose: () => void;
  onAdded: () => void;
}> = ({ open, workspaceId, workspaceName, onClose, onAdded }) => {
  const [form] = Form.useForm<AddForm>();
  const [roles, setRoles] = useState<RoleName[]>([]);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!open) return;
    getRoleNames().then(setRoles).catch(() => setRoles([]));
  }, [open, workspaceId]);

  const submit = async (values: AddForm) => {
    setAdding(true);
    try {
      await addWorkspaceMember(workspaceId, {
        user_id: values.userId,
        ...roleBody(values.roleKey)
      });
      message.success('Member added to this workspace.');
      form.resetFields();
      onAdded();
      onClose();
    } catch (error: any) {
      message.error(grantMessage(error) ?? error?.response?.data?.detail ?? 'The member could not be added.');
    } finally {
      setAdding(false);
    }
  };

  return (
    <Modal
      title="Add from organisation"
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={submit}
      >
        <Form.Item label="Workspace">
          <Input value={workspaceName} disabled />
        </Form.Item>
        <Form.Item
          label="Person"
          name="userId"
          rules={[{
            required: true,
            message: 'Choose a person from your organisation'
          }]}
          extra="People who already belong to this organisation and are not in this workspace yet. To bring in someone new, invite them to the organisation."
        >
          <PersonPicker
            mode="single"
            scope={{
              kind: 'node',
              nodeId: workspaceId,
              purpose: 'invite'
            }}
            placeholder="Search by name or email"
          />
        </Form.Item>
        <Form.Item
          label="Role"
          name="roleKey"
          rules={[{ required: true }]}
        >
          <Select
            options={roles.filter((role) => role.assignable !== false).map((role) => ({
              value: roleValue(role),
              label: role.name
            }))}
          />
        </Form.Item>
        <Button
          htmlType="submit"
          type="primary"
          block
          loading={adding}
        >Add to workspace</Button>
      </Form>
    </Modal>
  );
};

export default AddFromOrganisation;
